/**
 * An email address that survives Cloudflare's Email Address Obfuscation.
 *
 * Scrape Shield rewrites every `mailto:` it finds in the served HTML into
 * `<span class="__cf_email__">[email protected]</span>`. The legal pages are
 * pre-rendered, so that rewritten HTML is exactly what Google's and Meta's
 * reviewers read without JavaScript: the Grievance Officer and the account
 * deletion contact reached them as the literal string "[email protected]".
 * Cloudflare leaves alone anything between `<!--email_off-->` and
 * `<!--/email_off-->`.
 *
 * React strips JSX comments, so the markers have to be emitted as raw HTML.
 * `dangerouslySetInnerHTML` is not diffed during hydration, so the browser
 * keeps the server's markup and the comments stay in the document.
 */

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function EmailLink({
  address,
  href,
  label,
  className,
}: {
  /** The address itself; also the default visible text. */
  address: string;
  /** Full href when the link carries a subject or body. Defaults to `mailto:<address>`. */
  href?: string;
  /** Visible text, when it should not be the address. */
  label?: string;
  className?: string;
}) {
  const attrs = [
    className ? `class="${escapeHtml(className)}"` : null,
    `href="${escapeHtml(href ?? `mailto:${address}`)}"`,
  ].filter(Boolean).join(" ");

  const html =
    `<!--email_off--><a ${attrs}>${escapeHtml(label ?? address)}</a><!--/email_off-->`;

  // `display: contents` keeps the wrapper out of layout, so the anchor styles
  // exactly as it did when it was a direct child.
  return <span style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: html }} />;
}
